import { useState, useEffect } from "react";
import { H1Title, Paragraph } from "../../components/typography/typography";
import { AppButton } from "../../components/buttons/button";
import Modal from "../../components/modal/modal";
import CompetitionService from "../../services/competitionService";
import SubmissionService from "../../services/submissionService";
import { Success, Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faForward, faFileAlt, faCheckCircle, faClock, faTimesCircle, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

const ROUND_KEY = { 1: 'round1', 2: 'round2', 3: 'round3', 4: 'round4', 5: 'round5', 6: 'round6' };
const NEXT_ROUND_KEY = { 1: 'round2', 2: 'round3', 3: 'round4', 4: 'round5', 5: 'round6', 6: 'implementation' };

const ROUND_STATUS_STYLE = {
  upcoming: { bg: 'bg-grey-200', text: 'text-grey-667', icon: faClock },
  active: { bg: 'bg-primary-bg', text: 'text-primary', icon: faPlay },
  review: { bg: 'bg-yellow-500/10', text: 'text-orange-dark', icon: faClock },
  completed: { bg: 'bg-green-light', text: 'text-green', icon: faCheckCircle },
};

export default function AdminCompetition() {
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [docs, setDocs] = useState({ competitionDescription: '', judgingRubric: '', locationBrief: '' });
  const [savingDocs, setSavingDocs] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSubs, setPreviewSubs] = useState([]);
  const [actionMap, setActionMap] = useState({}); // submissionId → 'advance' | 'eliminate' | 'skip'
  const [notify, setNotify] = useState({ enabled: true, advanceTitle: '', advanceBody: '', eliminateTitle: '', eliminateBody: '' });
  const [committing, setCommitting] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => { loadCompetition(); }, []);

  const loadCompetition = async () => {
    try {
      const { data } = await CompetitionService.getActive();
      const comp = data.data.competition;
      setCompetition(comp);
      setDocs({
        competitionDescription: comp.documents?.competitionDescription || '',
        judgingRubric: comp.documents?.judgingRubric || '',
        locationBrief: comp.documents?.locationBrief || ''
      });
    } catch {
      // No active competition — we can create one
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      const { data } = await CompetitionService.create({
        name: 'The Human Development Lab',
        year: new Date().getFullYear(),
        description: 'Design, fund, and implement a humanitarian project in Kenya.'
      });
      setCompetition(data.data.competition);
      Success('Competition created');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to create');
    }
  };

  const handleOpenRegistration = async () => {
    setOpening(true);
    try {
      const { data } = await CompetitionService.openRegistration(competition._id);
      setCompetition(data.data.competition);
      Success('Registration opened — Round 1 is now active');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed');
    } finally { setOpening(false); }
  };

  const openAdvancePreview = async () => {
    const currentKey = ROUND_KEY[competition.currentRound];
    const nextKey = NEXT_ROUND_KEY[competition.currentRound];
    if (!currentKey || !nextKey) { ToastError('No further rounds to advance'); return; }

    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const { data } = await SubmissionService.getAll({
        competitionId: competition._id,
        currentRound: currentKey,
        includeScores: 'true',
        limit: 500
      });
      const subs = data.data.submissions || [];
      setPreviewSubs(subs);

      const initialActions = {};
      subs.forEach(s => {
        const scoreForRound = s.scoring?.[currentKey];
        if (scoreForRound && scoreForRound.averageMeetsThreshold) initialActions[s._id] = 'advance';
        else if (scoreForRound && !scoreForRound.averageMeetsThreshold) initialActions[s._id] = 'eliminate';
        else initialActions[s._id] = 'skip';
      });
      setActionMap(initialActions);

      setNotify({
        enabled: true,
        advanceTitle: `You've advanced to ${nextKey === 'implementation' ? 'Implementation' : 'Round ' + nextKey.replace('round', '')}!`,
        advanceBody: `Great news! Your submission has advanced. Log in to your portal for next steps.`,
        eliminateTitle: `Update on your submission`,
        eliminateBody: `Thank you for participating in The Human Development Lab. After careful review, your submission was not selected to advance to the next round. We appreciate the effort you put into your proposal.`
      });
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to load submissions');
      setPreviewOpen(false);
    } finally { setPreviewLoading(false); }
  };

  const setAction = (id, action) => setActionMap(prev => ({ ...prev, [id]: action }));

  const handleReset = async () => {
    setResetting(true);
    try {
      const { data } = await CompetitionService.reset(competition._id);
      setCompetition(data.data.competition);
      Success('Competition reset to draft — all submissions, teams, and notifications cleared');
      setResetOpen(false);
      setResetConfirmText('');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to reset');
    } finally { setResetting(false); }
  };

  const commitAdvance = async () => {
    const nextKey = NEXT_ROUND_KEY[competition.currentRound];
    const advanceIds = Object.entries(actionMap).filter(([, a]) => a === 'advance').map(([id]) => id);
    const eliminateIds = Object.entries(actionMap).filter(([, a]) => a === 'eliminate').map(([id]) => id);

    setCommitting(true);
    try {
      if (advanceIds.length) {
        await SubmissionService.bulkAdvance({
          submissionIds: advanceIds,
          toRound: nextKey,
          notification: notify.enabled && notify.advanceTitle.trim() ? { title: notify.advanceTitle.trim(), body: notify.advanceBody.trim() } : null
        });
      }
      if (eliminateIds.length) {
        await SubmissionService.bulkEliminate({
          submissionIds: eliminateIds,
          notification: notify.enabled && notify.eliminateTitle.trim() ? { title: notify.eliminateTitle.trim(), body: notify.eliminateBody.trim() } : null
        });
      }
      const { data } = await CompetitionService.advanceRound(competition._id);
      setCompetition(data.data.competition);
      Success(`Round advanced — ${advanceIds.length} advanced, ${eliminateIds.length} eliminated`);
      setPreviewOpen(false);
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to advance');
    } finally { setCommitting(false); }
  };

  const handleSaveDocs = async () => {
    setSavingDocs(true);
    try {
      const { data } = await CompetitionService.update(competition._id, { documents: docs });
      setCompetition(data.data.competition);
      Success('Competition documents saved');
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed');
    } finally { setSavingDocs(false); }
  };

  if (loading) return <div className="py-20 text-center text-grey-667">Loading...</div>;

  if (!competition) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <H1Title size="sm">No Active Competition</H1Title>
        <Paragraph className="text-grey-667 mt-2 mb-6">Create The Human Development Lab competition for this year.</Paragraph>
        <AppButton onClick={handleCreate} fullWidth={false} className="px-8">Create Competition</AppButton>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <H1Title size="sm">{competition.name}</H1Title>
          <Paragraph size="sm" className="text-grey-667">{competition.year} &middot; Status: {competition.status}</Paragraph>
        </div>
        <div className="flex gap-2">
          {competition.status === 'draft' && (
            <AppButton onClick={handleOpenRegistration} loading={opening} fullWidth={false} size="sm" className="px-4">
              <FontAwesomeIcon icon={faPlay} className="mr-1" /> Open Registration
            </AppButton>
          )}
          {['registration_open', 'active'].includes(competition.status) && (
            <AppButton onClick={openAdvancePreview} loading={previewLoading && !previewOpen} fullWidth={false} size="sm" className="px-4">
              <FontAwesomeIcon icon={faForward} className="mr-1" /> Advance Round
            </AppButton>
          )}
          <AppButton onClick={() => setResetOpen(true)} variant="outline" fullWidth={false} size="sm" className="px-4 !text-red-500 !border-red-500">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1" /> Reset
          </AppButton>
        </div>
      </div>

      {/* Round Timeline */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <h3 className="font-bold mb-4">Round Timeline</h3>
        <p className="text-sm text-grey-667 mb-4">Current Round: <span className="font-bold text-primary">{competition.currentRound || 'Not started'}</span></p>
        <div className="space-y-3">
          {competition.rounds?.map((r) => {
            const style = ROUND_STATUS_STYLE[r.status] || ROUND_STATUS_STYLE.upcoming;
            const isCurrent = r.roundNumber === competition.currentRound;
            return (
              <div key={r.roundNumber} className={`flex items-center gap-4 p-3 rounded-lg ${isCurrent ? 'bg-primary-bg border border-primary/20' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${style.bg} ${style.text}`}>
                  <FontAwesomeIcon icon={style.icon} />
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${isCurrent ? 'text-primary' : 'text-grey-dark'}`}>
                    Round {r.roundNumber}: {r.name}
                  </p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full ${style.bg} ${style.text}`}>{r.status}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Competition Documents */}
      <div className="bg-white rounded-xl border border-grey-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">
            <FontAwesomeIcon icon={faFileAlt} className="mr-2 text-primary" /> Competition Documents
          </h3>
          <AppButton onClick={handleSaveDocs} loading={savingDocs} size="sm" fullWidth={false} className="px-4">
            Save Documents
          </AppButton>
        </div>
        <p className="text-xs text-grey-667 mb-4">Add links to documents that participants can download. Use Google Drive share links or direct PDF URLs.</p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">1. Comprehensive Competition Description</label>
            <input type="url" className="w-full border border-grey-200 rounded-md px-4 py-2.5 text-sm outline-none focus:border-primary" placeholder="https://drive.google.com/..." value={docs.competitionDescription} onChange={(e) => setDocs({ ...docs, competitionDescription: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">2. Judging Rubric</label>
            <input type="url" className="w-full border border-grey-200 rounded-md px-4 py-2.5 text-sm outline-none focus:border-primary" placeholder="https://drive.google.com/..." value={docs.judgingRubric} onChange={(e) => setDocs({ ...docs, judgingRubric: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">3. Location Brief</label>
            <input type="url" className="w-full border border-grey-200 rounded-md px-4 py-2.5 text-sm outline-none focus:border-primary" placeholder="https://drive.google.com/..." value={docs.locationBrief} onChange={(e) => setDocs({ ...docs, locationBrief: e.target.value })} />
          </div>
        </div>
      </div>

      {previewOpen && (
        <Modal handleClose={() => !committing && setPreviewOpen(false)} title={`Advance Round ${competition.currentRound} → ${NEXT_ROUND_KEY[competition.currentRound]}`}>
          <div className="space-y-4">
            {previewLoading ? (
              <p className="text-sm text-grey-667 text-center py-6">Loading submissions...</p>
            ) : previewSubs.length === 0 ? (
              <p className="text-sm text-grey-667 text-center py-6">No submissions in the current round.</p>
            ) : (
              <>
                <Paragraph size="sm" className="text-grey-667">
                  Review each submission's action before advancing. Defaults come from the average judge scores: submissions meeting the threshold (avg ≥ 3 on every criterion) are set to advance, those below are set to eliminate, and unscored are set to skip.
                </Paragraph>

                <div className="flex gap-3 text-xs">
                  <span className="bg-green-light text-green px-2 py-1 rounded-full font-semibold">
                    Advance: {Object.values(actionMap).filter(a => a === 'advance').length}
                  </span>
                  <span className="bg-red-500/10 text-red-500 px-2 py-1 rounded-full font-semibold">
                    Eliminate: {Object.values(actionMap).filter(a => a === 'eliminate').length}
                  </span>
                  <span className="bg-grey-200 text-grey-667 px-2 py-1 rounded-full font-semibold">
                    Skip: {Object.values(actionMap).filter(a => a === 'skip').length}
                  </span>
                </div>

                <div className="border border-grey-200 rounded-lg divide-y divide-grey-200 max-h-[300px] overflow-y-auto">
                  {previewSubs.map(s => {
                    const currentKey = ROUND_KEY[competition.currentRound];
                    const score = s.scoring?.[currentKey];
                    const action = actionMap[s._id] || 'skip';
                    return (
                      <div key={s._id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{s.submitterId?.firstName} {s.submitterId?.lastName}</p>
                          <p className="text-xs text-grey-98 truncate">
                            {s.teamId?.name || 'Individual'}
                            {score ? ` · ${score.total.toFixed(1)}/20 (${score.judgeCount} judge${score.judgeCount === 1 ? '' : 's'})` : ' · not scored'}
                            {score && (
                              <span className={`ml-2 font-semibold ${score.averageMeetsThreshold ? 'text-green' : 'text-red-500'}`}>
                                {score.averageMeetsThreshold ? '✓ meets threshold' : '✗ below threshold'}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex gap-1 flex-shrink-0 ml-3">
                          <button onClick={() => setAction(s._id, 'advance')} className={`text-xs px-2 py-1 rounded-full font-semibold cursor-pointer ${action === 'advance' ? 'bg-green text-white' : 'bg-grey-bg text-grey-667 hover:bg-green-light hover:text-green'}`}>
                            <FontAwesomeIcon icon={faCheckCircle} /> Advance
                          </button>
                          <button onClick={() => setAction(s._id, 'eliminate')} className={`text-xs px-2 py-1 rounded-full font-semibold cursor-pointer ${action === 'eliminate' ? 'bg-red-500 text-white' : 'bg-grey-bg text-grey-667 hover:bg-red-500/10 hover:text-red-500'}`}>
                            <FontAwesomeIcon icon={faTimesCircle} /> Eliminate
                          </button>
                          <button onClick={() => setAction(s._id, 'skip')} className={`text-xs px-2 py-1 rounded-full font-semibold cursor-pointer ${action === 'skip' ? 'bg-grey-667 text-white' : 'bg-grey-bg text-grey-667 hover:bg-grey-200'}`}>
                            Skip
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-grey-200 pt-4">
                  <label className="flex items-center gap-2 mb-3 cursor-pointer">
                    <input type="checkbox" checked={notify.enabled} onChange={(e) => setNotify({ ...notify, enabled: e.target.checked })} />
                    <span className="text-sm font-semibold">Send notifications</span>
                  </label>
                  {notify.enabled && (
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-bold text-green mb-1">Advance notification</p>
                        <input className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm mb-2 outline-none focus:border-primary" placeholder="Title" value={notify.advanceTitle} onChange={(e) => setNotify({ ...notify, advanceTitle: e.target.value })} />
                        <textarea className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm min-h-[60px] outline-none focus:border-primary resize-y" placeholder="Body" value={notify.advanceBody} onChange={(e) => setNotify({ ...notify, advanceBody: e.target.value })} />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-red-500 mb-1">Eliminate notification</p>
                        <input className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm mb-2 outline-none focus:border-primary" placeholder="Title" value={notify.eliminateTitle} onChange={(e) => setNotify({ ...notify, eliminateTitle: e.target.value })} />
                        <textarea className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm min-h-[60px] outline-none focus:border-primary resize-y" placeholder="Body" value={notify.eliminateBody} onChange={(e) => setNotify({ ...notify, eliminateBody: e.target.value })} />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="flex gap-2 justify-end pt-2 border-t border-grey-200">
              <AppButton onClick={() => setPreviewOpen(false)} variant="outline" size="sm" fullWidth={false} className="px-4" disabled={committing}>Cancel</AppButton>
              <AppButton onClick={commitAdvance} loading={committing} size="sm" fullWidth={false} className="px-4" disabled={previewLoading}>
                Confirm & Advance
              </AppButton>
            </div>
          </div>
        </Modal>
      )}

      {resetOpen && (
        <Modal handleClose={() => !resetting && (setResetOpen(false), setResetConfirmText(''))} title="Reset Competition">
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <p className="text-sm font-bold text-red-500 mb-2">
                <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
                This action cannot be undone.
              </p>
              <p className="text-sm text-grey-dark mb-2">The following will be permanently deleted:</p>
              <ul className="text-sm text-grey-667 list-disc list-inside space-y-1">
                <li>All submissions for this competition (drafts, submitted, advanced, eliminated)</li>
                <li>All teams and team invites</li>
                <li>All judge scores and judge assignments</li>
                <li>All notifications (admin activity feed included)</li>
              </ul>
              <p className="text-sm text-grey-dark mt-3">User accounts (participants, judges, admins) are kept, along with competition name, year, documents, and legal documents. The competition returns to <b>draft</b> state so you can open registration fresh.</p>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                Type <code className="bg-grey-bg px-1.5 py-0.5 rounded text-xs">{competition.name}</code> to confirm:
              </label>
              <input
                type="text"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                className="w-full border border-grey-200 rounded-md px-3 py-2 text-sm outline-none focus:border-red-500"
                placeholder={competition.name}
                autoFocus
              />
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-grey-200">
              <AppButton onClick={() => { setResetOpen(false); setResetConfirmText(''); }} variant="outline" size="sm" fullWidth={false} className="px-4" disabled={resetting}>Cancel</AppButton>
              <AppButton
                onClick={handleReset}
                loading={resetting}
                size="sm"
                fullWidth={false}
                className="px-4 !bg-red-500 !text-white hover:!bg-red-600"
                disabled={resetConfirmText !== competition.name}
              >
                Reset Competition
              </AppButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
